import {
  EHeadingStyleVariant,
  GeneralEyeNormalIcon,
  Heading,
  MiscellaneousPlayIcon,
} from '@outdoorsyco/bonfire';
import Head from 'next/head';
import React, { Fragment, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import GalleryItem from '@/components/route/listing/Gallery/GalleryList/GalleryItem';
import Lightbox from '@/components/switchback/Lightbox';
import ResponsiveImage from '@/components/utility/ResponsiveImage/ResponsiveImage';
import useQueryParams from '@/hooks/useQueryParams';
import { useRouter } from '@/hooks/useRouter';
import { TRootState } from '@/redux/rootReducer';
import { EImageTags, IImage } from '@/services/types/search/rentals/id';
import { useAiImageOptimization } from '@/utility/experiments';
import formatImageURL, { ImageSize } from '@/utility/formatImageURL';
import { sortImagesByPosition, sortImagesByPrimaryAndPosition } from '@/utility/rentals';
import { getYoutubePreviewImage } from '@/utility/youtubeParser';

import css from './GalleryList.module.css';

type TImage = IImage & { alt?: string };
type TFilterSlides = TRootState['filterSlides'];

interface IGalleryList {
  images: TImage[];
}

const GalleryList: React.FC<IGalleryList> = ({ images }) => {
  const router = useRouter();
  const filterSlides = useSelector<TRootState, TFilterSlides>(state => state.filterSlides);
  const category = filterSlides.category;

  const { isEnabled: isAiOptimizationEnabled, isLoading: isExperimentLoading } =
    useAiImageOptimization();

  const sortedImages = useMemo(
    () =>
      !category || category === EImageTags.ALL
        ? sortImagesByPrimaryAndPosition(images, isAiOptimizationEnabled)
        : category === EImageTags.VIDEO
          ? sortImagesByPosition(images, isAiOptimizationEnabled).filter(i => i.video)
          : sortImagesByPosition(images, isAiOptimizationEnabled).filter(
              i => i.category?.slug === category,
            ),
    [images, category, isAiOptimizationEnabled],
  );
  const { image: imageParam } = useQueryParams();
  const initialLightboxImageId = Number(imageParam);
  const initialLightboxImage = sortedImages.find(i => i.id === initialLightboxImageId) || null;
  const [internalOpenedImage, setInternalOpenedImage] = useState<IImage | null>(
    initialLightboxImage,
  );
  const isLightboxOpen = internalOpenedImage !== null;

  // Image can come from a new page load (initialLightboxImage) or a state change.
  const activeSlide = internalOpenedImage
    ? sortedImages.map(i => i.id).indexOf(internalOpenedImage.id)
    : 0;

  const beforeSlideChange = (_oldIndex: number, newIndex: number) => {
    const nextImage = sortedImages[newIndex];
    if (!nextImage) {
      return;
    }
    setInternalOpenedImage(nextImage);
    router.push({ query: { ...router.query, image: nextImage.id } }, undefined, { shallow: true });
  };

  const openLightBox = (image: TImage) => () => {
    setInternalOpenedImage(image);
    router.push({ query: { ...router.query, image: image.id } }, undefined, { shallow: true });
  };

  const closeLightbox = () => {
    setInternalOpenedImage(null);
    const query = { ...router.query };
    delete query.image;
    router.push({ query }, undefined, { shallow: true });
  };

  const getSrc = (img: IImage) => {
    return !img.video ? img.url : getYoutubePreviewImage(img.url);
  };

  const nextImage = sortedImages[activeSlide + 1] || sortedImages[0];
  const prevImage = sortedImages[activeSlide - 1] || sortedImages[sortedImages.length - 1];
  const siblingImages = [
    nextImage && !nextImage.video && getSrc(nextImage),
    prevImage && !prevImage.video && getSrc(prevImage),
  ];
  const preloadSizes: ImageSize[] = ['square20', 'landscape1440'];

  const cols = [sortedImages.filter((_, i) => !(i % 2)), sortedImages.filter((_, i) => i % 2)];

  return (
    <>
      {isLightboxOpen && (
        <Head key="imagepreload">
          {siblingImages.map(sibSrc => {
            return (
              !!sibSrc &&
              preloadSizes.map(size => {
                return (
                  <link
                    key={sibSrc + size}
                    href={formatImageURL(sibSrc, size, 'fit')}
                    rel="preload"
                    as="image"
                  />
                );
              })
            );
          })}
        </Head>
      )}
      <div className={css.wrapper}>
        {isExperimentLoading ? (
          // Show loading placeholder during experiment loading to prevent blinking
          <div className="flex items-center justify-center h-64 col-span-2">
            <div className="text-gray-500 animate-pulse">
              <FormattedMessage
                id="Ve/vg1"
                defaultMessage="Loading images..."
                description="Loading text shown while AI experiment loads on photos page"
              />
            </div>
          </div>
        ) : (
          cols.map((imgs, idx) => {
            return (
              <div key={idx} className={css.col}>
                {imgs.map((image, index) => {
                  // To prevent CLS, only the first 4 images are loaded immediately
                  // The first and second cols images shift the layout when they load, pushing other images downward.
                  // To avoid this, loaders for the remaining images are not displayed initially.
                  const progressive = index > 4;
                  return (
                    <React.Fragment key={image.id}>
                      {image.odnMedia ? (
                        <figure className={css.imageWrapper}>
                          <div className="relative">
                            <ResponsiveImage
                              priority
                              progressive={progressive}
                              onClick={openLightBox(image)}
                              className={`${css.image} ${imgs.length === 1 ? css.columnSpan : ''}`}
                              src={image.url}
                              sourceSizes={['square720']}
                              cropMode="fit"
                              alt={image.alt ?? image.ai_description ?? image.description}
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black pointer-events-none bg-opacity-50">
                              <div className="flex flex-col items-center justify-center text-center text-white gap-2">
                                {image.odnMedia.is360Tour ? (
                                  <GeneralEyeNormalIcon className="text-2xl" />
                                ) : (
                                  <MiscellaneousPlayIcon className="w-12 h-12" />
                                )}

                                {image.odnMedia.is360Tour && (
                                  <Heading variant={EHeadingStyleVariant.H5}>
                                    <FormattedMessage
                                      defaultMessage="Explore 360 tour"
                                      id="3r33Bh"
                                    />
                                  </Heading>
                                )}
                              </div>
                            </div>
                          </div>
                        </figure>
                      ) : (
                        <figure className={css.imageWrapper}>
                          <div className="relative">
                            <ResponsiveImage
                              priority
                              progressive={progressive}
                              onClick={openLightBox(image)}
                              className={`${css.image} ${imgs.length === 1 ? css.columnSpan : ''}`}
                              src={getSrc(image)}
                              sourceSizes={['square720']}
                              cropMode="fit"
                              alt={image.alt ?? image.ai_description ?? image.description}
                              enhanceImage
                            />
                            {image.video && (
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <MiscellaneousPlayIcon className="w-12 h-12" />
                              </div>
                            )}
                          </div>
                        </figure>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })
        )}
        {!isExperimentLoading && (
          <Lightbox
            infinite
            dots={false}
            adaptiveHeight
            headerPagination
            lazyLoad="ondemand"
            customModalClass={css.lightBoxModal}
            className={css.lightBox}
            open={isLightboxOpen}
            initialSlide={activeSlide}
            activeSlide={activeSlide}
            beforeSlideChange={beforeSlideChange}
            onClose={closeLightbox}>
            {sortedImages.map((image, index) => (
              <Fragment key={image.url}>
                <GalleryItem
                  image={image.url}
                  description={image.description}
                  ai_description={image.ai_description}
                  isVideo={image.video}
                  index={index}
                  odnMedia={image.odnMedia}
                />
                {image.description && <div className={css.description}>{image.description}</div>}
              </Fragment>
            ))}
          </Lightbox>
        )}
      </div>
    </>
  );
};

export default GalleryList;
